import type { CSSProperties } from 'react';
import { Show, ShowFirstMatching } from '@components/Show';
import type { Row } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

import { TableCell } from './TableCell';

interface TableRowProps<TData = any> {
  anyIsExpanded: boolean;
  headerHeight: number;
  isFirstRow?: boolean;
  isLastRow: boolean;
  row: Row<TData>;
}

/**
 * Enhanced table row component with comprehensive styling and interactions.
 * Renders individual table rows with support for sticky positioning, expandable content,
 * hover effects, distinct parent/child backgrounds, and proper accessibility attributes.
 */
export const TableRow = <TData,>({
  row,
  isFirstRow = false,
  isLastRow,
  headerHeight,
  anyIsExpanded,
}: TableRowProps<TData>) => {
  const isParent = !row.parentId;
  const isChild = !!row.parentId;
  const isSticky = isParent && anyIsExpanded;

  const styleProps: CSSProperties = {
    position: isSticky ? 'sticky' : 'relative',
    top: isSticky ? `calc(${headerHeight}px * 0.97)` : undefined,
  };

  return (
    <tr
      key={row.id}
      style={styleProps}
      data-testid={`table-row`}
      data-row-id={row.id}
      data-row-type={isParent ? 'parent' : 'child'}
      className={twMerge(
        'group transition-colors duration-150',
        'focus-within:bg-blue-50/75 focus-within:ring-1 focus-within:ring-blue-200 hover:bg-blue-50',
        isParent && 'bg-white',
        isChild && 'bg-gray-50/50 hover:bg-blue-50',
        isSticky && 'z-10 bg-white',
        !isLastRow && 'border-b border-gray-200',
        isFirstRow && 'border-t border-gray-200',
        isLastRow && 'shadow-sm',
      )}
    >
      {row.getVisibleCells().map((cell) => {
        const isStandaloneDataCell = cell.column.columnDef.meta?.isStandaloneDataCell;

        return (
          <ShowFirstMatching key={cell.id}>
            <Show when={isStandaloneDataCell}>
              <td
                className={clsx('px-3 py-4', cell.column.columnDef.meta?.dataCellClassName)}
                style={{ width: cell.column.getSize() }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            </Show>
            <Show when={!isStandaloneDataCell}>
              <TableCell key={cell.id} cell={cell} />
            </Show>
          </ShowFirstMatching>
        );
      })}
    </tr>
  );
};
